import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { C, alpha } from '../lib/theme.js';
import { Button, Card, EmptyState, ErrorLine, PageHeader } from '../ui/ui.js';
import { useToast } from '../ui/Toast.js';
import { apiFetch } from '../lib/api.js';
import { useOrg } from '../app/OrgContext.js';
import { Icon } from '../ui/icons.js';
import { codeToName } from '../lib/countries.js';
import {
  tenantSite,
  formatMoney,
  currencySymbol,
  isUnretryableBuildError,
  FALLBACK_DAILY_CEILING,
} from '@goss/types';
import { AudienceSummary } from '../components/AudienceBuckets.js';
import { MetaCleanup } from '../components/MetaCleanup.js';
import type {
  AudienceBucket,
  MetaAudience,
  CampaignLaunch,
  CampaignRoas,
  Connections,
  Creative,
  GeoResult,
  MediaItem,
  MetaPage,
  MetaPost,
  RoasReport,
} from '../lib/types.js';

const fmtNum = (n: number) => (n >= 1000 ? `${(n / 1000).toFixed(1)}k` : String(n));

// Amplify posts come from Meta's API (quota + latency, not Supabase egress — the
// thumbnails are Meta CDN URLs). Cache the result in localStorage so navigating
// away/back and even logout/login don't re-hit Meta; a Refresh button re-pulls on
// demand, and we only auto-refresh when the cache is genuinely stale.
const POSTS_STALE_MS = 12 * 60 * 60 * 1000; // 12h → auto-refresh (Meta thumb URLs expire)
interface PostCache {
  posts: MetaPost[];
  warnings: string[];
  fetchedAt: number;
}
const postsCacheKey = (orgId: string) => `basil.amplifyPosts.${orgId}`;
function readPostCache(orgId: string): PostCache | null {
  try {
    const raw = localStorage.getItem(postsCacheKey(orgId));
    if (!raw) return null;
    const c = JSON.parse(raw) as PostCache;
    return Array.isArray(c.posts) && typeof c.fetchedAt === 'number' ? c : null;
  } catch {
    return null;
  }
}
function writePostCache(orgId: string, c: PostCache): void {
  try {
    localStorage.setItem(postsCacheKey(orgId), JSON.stringify(c));
  } catch {
    // ignore quota / private-mode failures — cache is best-effort
  }
}
function agoLabel(ms: number): string {
  const s = Math.max(0, Math.floor((Date.now() - ms) / 1000));
  if (s < 60) return 'just now';
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

// Draft: the in-progress launch config persists so you never lose your setup.
type LaunchMode = 'amplify' | 'organic' | 'new' | 'partnership';
// Plain-language entry choices for the Launch page — each maps to a LaunchMode.
// Boost (amplify) leads: it's the most common job and keeps a post's organic
// likes/comments. "New ad" builds from a Basil post; "Post free" is organic.
interface ModeChoice {
  mode: LaunchMode;
  icon: 'boost' | 'sparkles' | 'megaphone' | 'users';
  title: string;
  desc: string;
}
const MODE_CHOICES: ModeChoice[] = [
  {
    mode: 'amplify',
    icon: 'boost',
    title: 'Boost a post you already published',
    desc: 'Put budget behind an Instagram / Facebook post. Keeps its likes & comments.',
  },
  {
    mode: 'partnership',
    icon: 'users',
    title: "Run a creator's post (partnership ad)",
    desc: "For a collab or paid partnership. The creator sends you a code — paste it here. These posts can't be boosted normally.",
  },
  {
    mode: 'new',
    icon: 'sparkles',
    title: 'Launch a new ad from a Basil post',
    desc: 'Turn a post you made here (image + copy) into a fresh paid ad.',
  },
  {
    mode: 'organic',
    icon: 'megaphone',
    title: 'Post for free (no ad spend)',
    desc: 'Publish a Basil post to your Page / Instagram organically, no budget.',
  },
];
interface LaunchDraft {
  mode: LaunchMode;
  creativeId: string | null;
  selectedPostId: string | null;
  pageId: string;
  link: string;
  budget: number;
  override: boolean;
  cities: GeoResult[];
  name: string;
  startDate: string;
  endDate: string;
  ctaType: string;
  // The chosen audience MUST persist: without it a reload silently fell back to
  // the locked Standard, so you could build to the wrong audience believing your
  // lookalike was still selected. Money bug — never let this go unsaved.
  audienceSel: string;
  wholeCountry: boolean;
  // Partnership ads: the creator's code. No caption/headline — the code resolves
  // to the creator's real post, which brings its own text and engagement.
  partnershipCode: string;
}
// The draft is PER WORKSPACE. It was one global key, so switching workspace carried
// the previous tenant's destination URL, Page, cities, budget and selected post into
// the next tenant's Launch form — the same class of leak as the Connections inputs.
const draftKey = (orgId: string) => `basil:launch:v1:${orgId}`;
const LEGACY_DRAFT_KEY = 'basil:launch:v1';
// The CTA button on the ad. Learn More is the on-brand default (routes to the
// blog); the rest are available per the owner's choice to allow them.
const CTA_OPTIONS: { value: string; label: string }[] = [
  { value: 'LEARN_MORE', label: 'Learn More (default)' },
  { value: 'BOOK_TRAVEL', label: 'Book Now' },
  { value: 'SIGN_UP', label: 'Sign Up' },
  { value: 'GET_OFFER', label: 'Get Offer' },
  { value: 'SHOP_NOW', label: 'Shop Now' },
];
function loadDraft(orgId: string | null): Partial<LaunchDraft> {
  // Drop the pre-per-org draft outright rather than migrating it: we can't know which
  // workspace it belonged to, and guessing wrong is exactly the bug being fixed.
  try {
    localStorage.removeItem(LEGACY_DRAFT_KEY);
  } catch {
    /* ignore */
  }
  if (!orgId) return {};
  try {
    return JSON.parse(localStorage.getItem(draftKey(orgId)) || '{}') as Partial<LaunchDraft>;
  } catch {
    return {};
  }
}

// Basil Bot pre-fill handoff: the bot writes a boost draft here, we read it ONCE
// on mount (then clear it) and fill the form. Keep the key in sync with BasilBot.
const PREFILL_KEY = 'basil:launch:prefill';
interface LaunchPrefill {
  mode?: 'amplify' | 'new';
  budget?: number | null;
  cities?: string[];
  post?: string | null;
  platform?: string | null;
  audience?: string | null;
}
function takePrefill(): LaunchPrefill | null {
  try {
    const raw = localStorage.getItem(PREFILL_KEY);
    if (!raw) return null;
    localStorage.removeItem(PREFILL_KEY);
    return JSON.parse(raw) as LaunchPrefill;
  } catch {
    return null;
  }
}
function saveDraft(orgId: string, d: LaunchDraft) {
  try {
    localStorage.setItem(draftKey(orgId), JSON.stringify(d));
  } catch {
    /* ignore */
  }
}

export function Launch() {
  const { orgId, activeOrg } = useOrg();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [editId, setEditId] = useState<string | null>(null);
  const [draft, setDraft] = useState('');
  const [confirmDelCr, setConfirmDelCr] = useState<string | null>(null);
  const [creatives, setCreatives] = useState<Creative[]>([]);
  const [mediaUrls, setMediaUrls] = useState<Record<string, string>>({});
  const [pages, setPages] = useState<MetaPage[]>([]);
  const [launches, setLaunches] = useState<CampaignLaunch[]>([]);
  const [roas, setRoas] = useState<RoasReport | null>(null);
  const [adAccount, setAdAccount] = useState<string>('');
  // Money is rendered in THIS workspace's currency, never a house default. Every
  // amount on this page used to be hardcoded '฿', so Propty (GBP, its own card)
  // read its budget, spend and revenue in baht.
  const money = useCallback(
    (n: number) => formatMoney(n, activeOrg?.default_currency),
    [activeOrg],
  );
  // The org's own ceiling, from budget_settings. Starts at the conservative
  // fallback so a slow/failed load can only ever be STRICTER than the truth,
  // never looser — an over-permissive default is the bug being fixed.
  const [dailyCeiling, setDailyCeiling] = useState<number>(FALLBACK_DAILY_CEILING);
  const [error, setError] = useState<string | null>(null);

  // Initial hydration only (mount-time orgId — it comes from localStorage, so it is
  // already known here). A later orgId change is handled by the switch-workspace
  // effect below, which re-hydrates from the new workspace's own draft.
  const initialOrgId = useRef(orgId);
  const savedDraft = useMemo(() => loadDraft(initialOrgId.current), []);
  // Which workspace the form currently holds. While this lags orgId we are mid-switch:
  // the draft-backed fields still hold the previous tenant's values, so nothing may be
  // persisted or prefilled until they've been re-hydrated.
  const [formOrgId, setFormOrgId] = useState(orgId);
  // A ?mode= deep-link (e.g. from Home) wins over the last-used draft mode.
  const [mode, setMode] = useState<LaunchMode>(() => {
    const m = new URLSearchParams(window.location.search).get('mode');
    return m === 'amplify' || m === 'new' || m === 'organic' || m === 'partnership'
      ? m
      : (savedDraft.mode ?? 'organic');
  });
  // A ?creative= deep-link (Compose's "Send to Launch →") preselects that post.
  const [creativeId, setCreativeId] = useState<string | null>(() => {
    const c = new URLSearchParams(window.location.search).get('creative');
    return c || savedDraft.creativeId || null;
  });
  const [selectedPostId, setSelectedPostId] = useState<string | null>(
    savedDraft.selectedPostId ?? null,
  );
  const [partnershipCode, setPartnershipCode] = useState(savedDraft.partnershipCode ?? '');
  const [posts, setPosts] = useState<MetaPost[]>([]);
  const [postWarnings, setPostWarnings] = useState<string[]>([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [postsLoaded, setPostsLoaded] = useState(false);
  const [postsFetchedAt, setPostsFetchedAt] = useState<number | null>(null);
  // Amplify grid: platform filter + how many to show at once (Show more reveals +6).
  const POSTS_PAGE = 6;
  const [postPlatform, setPostPlatform] = useState<'all' | 'facebook' | 'instagram'>('all');
  const [postLimit, setPostLimit] = useState(POSTS_PAGE);
  const [postSort, setPostSort] = useState<'recent' | 'top'>('recent');
  const [winnersOnly, setWinnersOnly] = useState(false);
  // Organic publishing: chosen post, target platforms, and the publish result.
  const [pubCreativeId, setPubCreativeId] = useState<string | null>(null);
  const [pubFB, setPubFB] = useState(true);
  const [pubIG, setPubIG] = useState(true);
  const [publishing, setPublishing] = useState(false);
  const [confirmPublish, setConfirmPublish] = useState(false);
  const [pubResult, setPubResult] = useState<{
    published: { platform: string; permalink: string | null }[];
    errors: { platform: string; message: string }[];
  } | null>(null);
  const [pageId, setPageId] = useState<string>(savedDraft.pageId ?? '');
  const [link, setLink] = useState(savedDraft.link ?? '');
  const [budget, setBudget] = useState(savedDraft.budget ?? 500);
  const [override, setOverride] = useState(savedDraft.override ?? false);
  const [cities, setCities] = useState<GeoResult[]>(savedDraft.cities ?? []);
  // Optional campaign name. Auto-suggested from the chosen post/creative, but
  // fully editable; blank means Basil auto-names it server-side.
  const [name, setName] = useState(savedDraft.name ?? '');
  const [startDate, setStartDate] = useState(savedDraft.startDate ?? '');
  const [endDate, setEndDate] = useState(savedDraft.endDate ?? '');
  const [ctaType, setCtaType] = useState(savedDraft.ctaType ?? 'LEARN_MORE');
  const nameTouched = useRef(false);
  // Audience selection. The dropdown value is "" (locked standard), "b:<id>" (a
  // Basil bucket — carries its own geo), or "m:<id>" (a Meta custom/lookalike
  // audience — layers on the city picker below).
  const [buckets, setBuckets] = useState<AudienceBucket[]>([]);
  const [metaAudiences, setMetaAudiences] = useState<MetaAudience[]>([]);
  const [audienceSel, setAudienceSel] = useState<string>(savedDraft.audienceSel ?? '');
  const bucketId = audienceSel.startsWith('b:') ? audienceSel.slice(2) : null;
  const metaAudId = audienceSel.startsWith('m:') ? audienceSel.slice(2) : null;
  const activeBucket = buckets.find((x) => x.id === bucketId) ?? null;
  const activeMetaAud = metaAudiences.find((x) => x.id === metaAudId) ?? null;
  // For a lookalike/custom audience with a known country, you can target the
  // whole country instead of cities (the audience is the precision, geo the
  // boundary). Defaults on when we know the country.
  const [wholeCountry, setWholeCountry] = useState(savedDraft.wholeCountry ?? true);
  const metaCountry = activeMetaAud?.country ?? null;
  const [geoQuery, setGeoQuery] = useState('');
  const [geoResults, setGeoResults] = useState<GeoResult[]>([]);
  const [highlight, setHighlight] = useState(0);
  const [searching, setSearching] = useState(false);
  const [building, setBuilding] = useState(false);
  const [msg, setMsg] = useState<string | null>(null);
  // A post caption snippet Basil asked us to pre-select once the posts load.
  const [prefillPost, setPrefillPost] = useState<{ hint: string; platform: string | null } | null>(
    null,
  );
  // An audience name Basil asked us to select once buckets/Meta audiences load.
  const [prefillAudience, setPrefillAudience] = useState<string | null>(null);
  const prefillDone = useRef(false);
  const [confirmLaunch, setConfirmLaunch] = useState<string | null>(null);
  const [confirmDel, setConfirmDel] = useState<string | null>(null);
  const [confirmPause, setConfirmPause] = useState<string | null>(null);
  const [busyLaunch, setBusyLaunch] = useState<string | null>(null);

  const load = useCallback(() => {
    if (!orgId) return;
    void apiFetch<{ buckets: AudienceBucket[] }>(`/api/orgs/${orgId}/audience-buckets`)
      .then((d) => setBuckets(d.buckets))
      .catch(() => {});
    void apiFetch<{ audiences: MetaAudience[] }>(`/api/orgs/${orgId}/meta/audiences`)
      .then((d) => setMetaAudiences(d.audiences))
      .catch(() => {});
    void apiFetch<{ creatives: Creative[] }>(`/api/orgs/${orgId}/creatives`)
      .then((d) => setCreatives(d.creatives.filter((c) => c.media_id)))
      .catch(() => {});
    void apiFetch<{ media: MediaItem[] }>(`/api/orgs/${orgId}/media`)
      .then((d) => {
        const m: Record<string, string> = {};
        d.media.forEach((x) => {
          if (x.url) m[x.id] = x.url;
        });
        setMediaUrls(m);
      })
      .catch(() => {});
    void apiFetch<{ launches: CampaignLaunch[] }>(`/api/orgs/${orgId}/campaigns`)
      .then((d) => setLaunches(d.launches))
      .catch(() => {});
    void apiFetch<RoasReport>(`/api/orgs/${orgId}/roas`)
      .then(setRoas)
      .catch(() => {});
    // Remembered default Page (works even when discovery can't list pages).
    void apiFetch<Connections>(`/api/orgs/${orgId}/connections`)
      .then((d) => {
        if (d.meta.page_id) setPageId((cur) => cur || (d.meta.page_id as string));
        setAdAccount((d.meta.ad_account_id ?? '').replace(/^act_/, ''));
      })
      .catch(() => {});
    void apiFetch<{ pages: MetaPage[] }>(`/api/orgs/${orgId}/meta/pages`)
      .then((d) => {
        setPages(d.pages);
        const pc = d.pages.find(
          (p) => p.username === 'phuketcleanse' || /phuket\s*cleanse/i.test(p.name),
        );
        if (pc) setPageId(pc.id);
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load pages'));
  }, [orgId]);

  async function saveDefaultPage() {
    if (!orgId || !pageId.trim()) return;
    try {
      await apiFetch(`/api/orgs/${orgId}/meta/page`, {
        method: 'PUT',
        body: JSON.stringify({ page_id: pageId.trim() }),
      });
      setMsg('Saved. This Page will auto-fill from now on.');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save page');
    }
  }
  useEffect(() => {
    load();
  }, [load]);

  // The daily-budget ceiling is per workspace. Re-fetched on every org change so
  // switching workspace can never leave the previous tenant's ceiling armed — the
  // same class of leak as the launch draft. Reset to the conservative fallback
  // first, so the window between switch and response is strict, not permissive.
  useEffect(() => {
    if (!orgId) return;
    let cancelled = false;
    setDailyCeiling(FALLBACK_DAILY_CEILING);
    apiFetch<{ budget: { daily_ceiling: number | null } | null }>(`/api/orgs/${orgId}/settings`)
      .then((d) => {
        const v = Number(d.budget?.daily_ceiling);
        if (!cancelled && Number.isFinite(v) && v > 0) setDailyCeiling(v);
      })
      .catch(() => {
        /* keep the conservative fallback; the Worker enforces the real ceiling anyway */
      });
    return () => {
      cancelled = true;
    };
  }, [orgId]);

  // Switching workspace re-hydrates every draft-backed field from the NEW workspace's
  // own draft. Without this the previous tenant's destination URL, Page, cities, budget
  // and selected post stayed on screen — and a build would spend against them.
  useEffect(() => {
    if (formOrgId === orgId) return;
    const d = loadDraft(orgId);
    setMode(d.mode ?? 'organic');
    setCreativeId(d.creativeId ?? null);
    setSelectedPostId(d.selectedPostId ?? null);
    setPageId(d.pageId ?? '');
    setLink(d.link ?? '');
    setBudget(d.budget ?? 500);
    setOverride(d.override ?? false);
    setCities(d.cities ?? []);
    setName(d.name ?? '');
    setStartDate(d.startDate ?? '');
    setEndDate(d.endDate ?? '');
    setCtaType(d.ctaType ?? 'LEARN_MORE');
    setAudienceSel(d.audienceSel ?? '');
    setWholeCountry(d.wholeCountry ?? true);
    setPartnershipCode(d.partnershipCode ?? '');
    nameTouched.current = false;
    // The post LIST is server state, not draft state, so re-hydrating the draft above
    // left it untouched: you saw the previous tenant's Facebook/Instagram posts under
    // the new workspace's name. Worse, the loader effect below is guarded on
    // `postsLoaded`, which stayed true across the switch — so it never refetched and
    // the stale grid persisted until someone happened to press Refresh. Clearing
    // `postsLoaded` re-arms that effect, which then reads THIS org's cache or fetches.
    setPosts([]);
    setPostWarnings([]);
    setPostsFetchedAt(null);
    setPostsLoaded(false);
    setFormOrgId(orgId);
  }, [orgId, formOrgId]);

  // Prefill the destination with THIS tenant's own site (never another's). Only fills a
  // blank field, so a typed or drafted URL always wins.
  useEffect(() => {
    if (formOrgId !== orgId) return; // mid-switch: `link` is still the old tenant's
    const site = tenantSite(activeOrg?.slug);
    if (site) setLink((cur) => cur || site);
  }, [activeOrg, orgId, formOrgId]);

  // Persist the launch draft so the config survives navigation + reload.
  useEffect(() => {
    if (!orgId || formOrgId !== orgId) return; // never write one tenant's values under another's key
    saveDraft(orgId, {
      mode,
      creativeId,
      selectedPostId,
      pageId,
      link,
      budget,
      override,
      cities,
      name,
      startDate,
      endDate,
      ctaType,
      audienceSel,
      wholeCountry,
      partnershipCode,
    });
  }, [
    partnershipCode,
    mode,
    creativeId,
    selectedPostId,
    pageId,
    link,
    budget,
    override,
    cities,
    name,
    startDate,
    endDate,
    ctaType,
    audienceSel,
    wholeCountry,
    orgId,
    formOrgId,
  ]);

  // Suggest a campaign name from the chosen post/creative's first caption line.
  const suggestedName = useMemo(() => {
    const src =
      mode === 'amplify'
        ? posts.find((p) => p.id === selectedPostId)?.caption
        : creatives.find((c) => c.id === creativeId)?.primary_text;
    const line = src?.split('\n')[0]?.trim();
    if (!line) return '';
    return line.length > 60 ? line.slice(0, 58).trimEnd() + '…' : line;
  }, [mode, selectedPostId, creativeId, posts, creatives]);

  // Auto-fill the name with the suggestion until the user types their own.
  useEffect(() => {
    if (!nameTouched.current && suggestedName) setName(suggestedName);
  }, [suggestedName]);

  // Basil pre-fill: if the bot handed us a boost draft, apply it once. Cities are
  // resolved to Meta geo keys live; the post is matched after posts load below.
  useEffect(() => {
    if (prefillDone.current || !orgId) return;
    prefillDone.current = true;
    const pf = takePrefill();
    if (!pf) return;
    setMode(pf.mode === 'new' ? 'new' : 'amplify');
    if (typeof pf.budget === 'number' && pf.budget > 0) {
      setBudget(pf.budget);
      if (pf.budget > dailyCeiling) setOverride(true);
    }
    (pf.cities ?? []).slice(0, 10).forEach((name) => {
      void apiFetch<{ results: GeoResult[] }>(
        `/api/orgs/${orgId}/meta/geo-search?q=${encodeURIComponent(name)}`,
      )
        .then((d) => {
          const g = d.results?.[0];
          if (g) setCities((cs) => (cs.some((c) => c.key === g.key) ? cs : [...cs, g]));
        })
        .catch(() => {});
    });
    if (pf.post) setPrefillPost({ hint: pf.post, platform: pf.platform ?? null });
    if (pf.audience) setPrefillAudience(pf.audience);
    setMsg(
      'Basil pre-filled this boost for you. Review the details, then Build (it lands paused).',
    );
  }, [orgId]);

  // Once audiences load, select the one Basil named (a lookalike/custom audience,
  // a saved bucket, or Standard). Matched by name; leaves the picker if no match.
  useEffect(() => {
    if (!prefillAudience) return;
    const norm = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim();
    const wantN = norm(prefillAudience);
    if (wantN === 'standard' || wantN === '') {
      setAudienceSel('');
      setPrefillAudience(null);
      return;
    }
    const m = metaAudiences.find((a) => norm(a.name) === wantN || norm(a.name).includes(wantN));
    if (m) {
      setAudienceSel(`m:${m.id}`);
      setPrefillAudience(null);
      return;
    }
    const bk = buckets.find((b) => norm(b.name) === wantN || norm(b.name).includes(wantN));
    if (bk) {
      setAudienceSel(`b:${bk.id}`);
      setPrefillAudience(null);
      return;
    }
    // No match — once the lists have loaded, stop trying (leave the picker as is).
    if (metaAudiences.length > 0 || buckets.length > 0) setPrefillAudience(null);
  }, [prefillAudience, metaAudiences, buckets]);

  // Once the amplify posts have loaded, select the one Basil recommended by
  // matching its caption snippet (preferring the platform it suggested).
  useEffect(() => {
    if (!prefillPost || posts.length === 0) return;
    const hint = prefillPost.hint.toLowerCase().replace(/\s+/g, ' ').trim();
    const words = hint.split(' ').filter((w) => w.length > 3);
    const score = (p: MetaPost): number => {
      const cap = (p.caption || '').toLowerCase();
      if (!cap) return 0;
      if (hint && cap.includes(hint)) return 100;
      return words.reduce((s, w) => (cap.includes(w) ? s + 1 : s), 0);
    };
    let best: MetaPost | null = null;
    let bestScore = 0;
    for (const p of posts) {
      if (prefillPost.platform && p.platform !== prefillPost.platform) continue;
      const sc = score(p);
      if (sc > bestScore) {
        bestScore = sc;
        best = p;
      }
    }
    if (!best) {
      for (const p of posts) {
        const sc = score(p);
        if (sc > bestScore) {
          bestScore = sc;
          best = p;
        }
      }
    }
    if (best && bestScore > 0) setSelectedPostId(best.id);
    setPrefillPost(null);
  }, [prefillPost, posts]);

  // Pull recent FB/IG posts from Meta and cache them (survives navigation + login).
  const loadPosts = useCallback(() => {
    if (!orgId) return;
    setLoadingPosts(true);
    setPostsLoaded(true);
    void apiFetch<{ posts: MetaPost[]; warnings: string[] }>(`/api/orgs/${orgId}/meta/posts`)
      .then((d) => {
        const warnings = d.warnings ?? [];
        setPosts(d.posts);
        setPostWarnings(warnings);
        const fetchedAt = Date.now();
        setPostsFetchedAt(fetchedAt);
        writePostCache(orgId, { posts: d.posts, warnings, fetchedAt });
      })
      .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Could not load posts'))
      .finally(() => setLoadingPosts(false));
  }, [orgId]);

  // On entering Amplify: show the cached posts instantly; only hit Meta if there's
  // no cache or it's gone stale (so ancient/expired-thumbnail data self-heals).
  useEffect(() => {
    if (mode !== 'amplify' || postsLoaded || !orgId) return;
    const cached = readPostCache(orgId);
    if (cached) {
      setPosts(cached.posts);
      setPostWarnings(cached.warnings);
      setPostsFetchedAt(cached.fetchedAt);
      setPostsLoaded(true);
      if (Date.now() - cached.fetchedAt > POSTS_STALE_MS) loadPosts();
    } else {
      loadPosts();
    }
  }, [mode, postsLoaded, orgId, loadPosts]);

  // Live typeahead: search Meta as you type (debounced), populate the dropdown.
  useEffect(() => {
    const q = geoQuery.trim();
    if (q.length < 2 || !orgId) {
      setGeoResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    const t = setTimeout(() => {
      void apiFetch<{ results: GeoResult[] }>(
        `/api/orgs/${orgId}/meta/geo-search?q=${encodeURIComponent(q)}`,
      )
        .then((d) => {
          setGeoResults(d.results);
          setHighlight(0);
        })
        .catch((e: unknown) => setError(e instanceof Error ? e.message : 'Geo search failed'))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(t);
  }, [geoQuery, orgId]);

  function addCity(g: GeoResult) {
    if (!cities.find((c) => c.key === g.key)) setCities((cs) => [...cs, g]);
    setGeoResults([]);
    setGeoQuery('');
    setHighlight(0);
  }

  async function build() {
    if (!orgId) return;
    if (
      mode === 'amplify'
        ? !selectedPostId
        : mode === 'partnership'
          ? !partnershipCode.trim()
          : !creativeId
    )
      return;
    setBuilding(true);
    setError(null);
    setMsg(null);
    try {
      // A Basil bucket replaces geo; a Meta audience uses whole-country or cities.
      const audience = bucketId
        ? { audience_bucket_id: bucketId }
        : metaAudId
          ? {
              meta_audience_id: metaAudId,
              ...(useWholeCountry ? { country_keys: [metaCountry] } : {}),
            }
          : {};
      // Blank → Basil auto-names it server-side.
      const named = name.trim() ? { name: name.trim() } : {};
      // Every mode sends the same campaign settings; only the creative SOURCE
      // differs — a published post, a creator's code, or a Basil creative.
      const source =
        mode === 'amplify'
          ? { existing_post_id: selectedPostId }
          : mode === 'partnership'
            ? {
                partnership_ad_code: partnershipCode.trim(),
              }
            : { creative_id: creativeId };
      await apiFetch(`/api/orgs/${orgId}/campaigns/build`, {
        method: 'POST',
        body: JSON.stringify({
          ...source,
          page_id: pageId,
          link,
          daily_budget: budget,
          city_keys: cities.map((c) => c.key),
          ...audience,
          ...named,
          ...(startDate ? { start_date: startDate } : {}),
          ...(endDate ? { end_date: endDate } : {}),
          cta_type: ctaType,
          override,
        }),
      });
      setMsg('Campaign built in Meta (paused). Review below, then Go Live when ready.');
      toast('success', 'Campaign built in Meta — paused. Review it below, then Go Live.');
      load();
    } catch (e) {
      const m = e instanceof Error ? e.message : 'Build failed';
      setError(m);
      toast('error', `Build failed — ${m}`);
      load(); // surface the failed row (with Meta's reason) straight away
    } finally {
      setBuilding(false);
    }
  }

  async function deleteLaunch(id: string) {
    setError(null);
    try {
      await apiFetch(`/api/orgs/${orgId}/campaigns/${id}`, { method: 'DELETE' });
      toast('success', 'Removed from Basil and Meta Ads Manager.');
      load();
    } catch (e) {
      const m = e instanceof Error ? e.message : 'Delete failed';
      setError(m);
      toast('error', `Delete failed — ${m}`);
    }
  }

  // Resume a failed build with its EXACT original settings (audience, cities,
  // budget, dates) — replayed server-side, so nothing has to be re-picked here.
  async function retryBuild(id: string) {
    setError(null);
    setBusyLaunch(id);
    try {
      await apiFetch(`/api/orgs/${orgId}/campaigns/${id}/retry`, { method: 'POST' });
      toast('success', 'Rebuilt with your original settings — paused. Review below, then Go Live.');
      // The failed row has served its purpose; clear it so failures don't pile up.
      await apiFetch(`/api/orgs/${orgId}/campaigns/${id}`, { method: 'DELETE' }).catch(() => {});
      await load();
    } catch (e) {
      const m = e instanceof Error ? e.message : 'Retry failed';
      setError(m);
      toast('error', `Retry failed — ${m}`);
      await load();
    } finally {
      setBusyLaunch(null);
    }
  }

  // Publish a saved Post organically (non-ad) to the chosen platform(s).
  async function publishOrganicPost() {
    if (!orgId || !pubCreativeId || publishing) return;
    const platforms = [pubFB ? 'facebook' : null, pubIG ? 'instagram' : null].filter(
      (p): p is string => !!p,
    );
    if (platforms.length === 0) return;
    setPublishing(true);
    setError(null);
    setPubResult(null);
    try {
      const d = await apiFetch<{
        published: { platform: string; permalink: string | null }[];
        errors: { platform: string; message: string }[];
      }>(`/api/orgs/${orgId}/meta/organic`, {
        method: 'POST',
        body: JSON.stringify({ creative_id: pubCreativeId, platforms }),
      });
      setPubResult(d);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Publish failed');
    } finally {
      setPublishing(false);
      setConfirmPublish(false);
    }
  }

  // Inline caption edit + delete for a saved post (creative) — same as the Posts tab.
  async function saveCaption(id: string) {
    setEditId(null);
    try {
      await apiFetch(`/api/orgs/${orgId}/creatives/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ primary_text: draft }),
      });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    }
  }
  async function deleteCreative(id: string) {
    setConfirmDelCr(null);
    if (creativeId === id) setCreativeId(null);
    try {
      await apiFetch(`/api/orgs/${orgId}/creatives/${id}`, { method: 'DELETE' });
      load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  }

  async function goLive(id: string) {
    setError(null);
    setBusyLaunch(id);
    try {
      await apiFetch(`/api/orgs/${orgId}/campaigns/${id}/launch`, { method: 'POST' });
      setMsg('Campaign is live. Meta will review it, then it starts delivering.');
      toast('success', 'Campaign is LIVE — Meta reviews it, then delivery + spend start.');
      setConfirmLaunch(null);
      await load();
    } catch (e) {
      const m = e instanceof Error ? e.message : 'Launch failed';
      setError(m);
      toast('error', `Go Live failed — ${m}`);
    } finally {
      setBusyLaunch(null);
    }
  }

  // Pause a live campaign in Meta (stops spend). marketer+ enforced server-side.
  async function pauseCampaign(launchId: string, metaCampaignId: string) {
    setError(null);
    setMsg(null);
    setConfirmPause(null);
    setBusyLaunch(launchId);
    try {
      await apiFetch(`/api/orgs/${orgId}/meta/campaign/${metaCampaignId}/status`, {
        method: 'POST',
        body: JSON.stringify({ status: 'PAUSED' }),
      });
      setMsg('Campaign paused in Meta — spend stopped.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not pause');
    } finally {
      setBusyLaunch(null);
    }
  }

  const overCeiling = budget > dailyCeiling;
  // A Basil bucket carries its own geo; a Meta audience can use whole-country
  // (when known) or cities; standard needs cities.
  const useWholeCountry = !!metaCountry && wholeCountry;
  const geoOk = bucketId ? true : useWholeCountry ? true : cities.length > 0;
  // A boost needs the link too — its CTA button carries the destination URL.
  const canBuild =
    !!pageId &&
    geoOk &&
    !!link.trim() &&
    (!overCeiling || override) &&
    (mode === 'amplify'
      ? !!selectedPostId
      : mode === 'partnership'
        ? !!partnershipCode.trim()
        : !!creativeId);
  const roasBy = new Map<string, CampaignRoas>(
    (roas?.campaigns ?? []).map((c) => [c.campaign_id, c]),
  );
  const creativeById = new Map<string, Creative>(creatives.map((c) => [c.id, c]));
  // A campaign's name: the one it was built/renamed with (stored on the launch
  // row, same as Meta shows), else the first line of its source post's caption,
  // else a placeholder.
  const launchName = (l: CampaignLaunch): string => {
    if (l.name && l.name.trim()) return l.name.trim();
    const cr = l.creative_id ? creativeById.get(l.creative_id) : undefined;
    const line = cr?.primary_text?.split('\n')[0]?.trim();
    return line && line.length > 0 ? line : 'Untitled campaign';
  };

  // Amplify grid: counts per platform, the active filter/sort, and the capped slice.
  const fbCount = posts.filter((p) => p.platform === 'facebook').length;
  const igCount = posts.filter((p) => p.platform === 'instagram').length;
  const winnerCount = posts.filter((p) => p.outperforming).length;
  let filteredPosts =
    postPlatform === 'all' ? posts : posts.filter((p) => p.platform === postPlatform);
  if (winnersOnly) filteredPosts = filteredPosts.filter((p) => p.outperforming);
  if (postSort === 'top') {
    // Top-performing first: engagement is the always-available signal.
    filteredPosts = [...filteredPosts].sort((a, b) => (b.engagement ?? 0) - (a.engagement ?? 0));
  }
  // Cross-post grouping: in the "all" view, the same caption on Facebook + Instagram
  // is one piece of content — show it as a single card carrying both platform chips.
  // (Filtered views stay per-post so the platform tabs mean what they say.)
  const normCap = (s: string) => s.toLowerCase().replace(/\s+/g, ' ').trim().slice(0, 140);
  // A cross-post goes out to both platforms at once, so a shared caption only means
  // "same post" when the two were published close together.
  const PAIR_WINDOW_MS = 48 * 60 * 60 * 1000;
  const closeInTime = (g: MetaPost[], p: MetaPost) => {
    const a = g[0]?.created ? Date.parse(g[0].created) : NaN;
    const b = p.created ? Date.parse(p.created) : NaN;
    if (!Number.isFinite(a) || !Number.isFinite(b)) return true; // no dates — don't block
    return Math.abs(a - b) <= PAIR_WINDOW_MS;
  };
  const postGroups: MetaPost[][] = [];
  // Keep EVERY group for a caption, not just the newest. Reusing one slot meant a
  // caption used twice on Facebook orphaned the first post and paired the Instagram
  // copy with the wrong one — which is how a post that IS on Instagram came to be
  // labelled Facebook-only.
  const groupsByCap = new Map<string, MetaPost[][]>();
  for (const p of filteredPosts) {
    const cap = postPlatform === 'all' ? normCap(p.caption) : '';
    const candidates = cap ? (groupsByCap.get(cap) ?? []) : [];
    const match = candidates.find(
      (g) => !g.some((x) => x.platform === p.platform) && closeInTime(g, p),
    );
    if (match) {
      match.push(p);
    } else {
      const g = [p];
      postGroups.push(g);
      if (cap) groupsByCap.set(cap, [...candidates, g]);
    }
  }
  const visibleGroups = postGroups.slice(0, postLimit);

  return (
    <div style={{ padding: '26px 30px 40px', maxWidth: 1000, animation: 'basilfade .3s ease' }}>
      <PageHeader eyebrow="Built paused · nothing spends until you go live" title="Launch" />
      {error && <ErrorLine>{error}</ErrorLine>}

      {/* Pick the job first — plain language, one clear choice. Each card maps to
          a LaunchMode; the machinery below is unchanged. */}
      <div className="label-mono" style={{ margin: '4px 0 10px' }}>
        WHAT DO YOU WANT TO DO?
      </div>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
          gap: 10,
          marginBottom: 22,
        }}
      >
        {MODE_CHOICES.map((ch) => {
          const on = mode === ch.mode;
          return (
            <button
              key={ch.mode}
              type="button"
              onClick={() => setMode(ch.mode)}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: 12,
                padding: '15px 16px',
                borderRadius: 13,
                cursor: 'pointer',
                fontFamily: 'inherit',
                textAlign: 'left',
                border: `1.5px solid ${on ? C.basil : C.line}`,
                background: on ? alpha(C.basil, 10) : C.surface,
              }}
            >
              <span style={{ flexShrink: 0, display: 'flex', color: on ? C.basil : C.secondary }}>
                <Icon name={ch.icon} size={20} />
              </span>
              <div style={{ minWidth: 0 }}>
                <div style={{ fontSize: 13.5, fontWeight: 600, color: on ? C.basil : C.primary }}>
                  {ch.title}
                </div>
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 4, lineHeight: 1.45 }}>
                  {ch.desc}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {mode === 'amplify' && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 10,
              flexWrap: 'wrap',
              margin: '6px 0 4px',
            }}
          >
            <div className="label-mono">
              1 · Pick a post already published on Facebook / Instagram
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              {postsFetchedAt && !loadingPosts && (
                <span style={{ font: `500 10.5px 'IBM Plex Mono',monospace`, color: C.faint }}>
                  Updated {agoLabel(postsFetchedAt)}
                </span>
              )}
              <span
                onClick={() => !loadingPosts && loadPosts()}
                title="Re-pull your latest posts from Facebook / Instagram"
                style={{
                  fontSize: 11.5,
                  color: loadingPosts ? C.muted : C.basil,
                  cursor: loadingPosts ? 'default' : 'pointer',
                  fontWeight: 600,
                }}
              >
                {loadingPosts ? 'Refreshing…' : '↻ Refresh'}
              </span>
            </div>
          </div>
          <div style={{ fontSize: 12, color: C.faint, marginBottom: 12 }}>
            Promote an already-published post as an ad — it keeps its organic likes &amp; comments.
            Not the Boost button: full targeting, budget guardrails, paused-first.
          </div>
          {postWarnings.length > 0 && (
            <div style={{ fontSize: 12, color: C.watch, marginBottom: 10, lineHeight: 1.5 }}>
              {postWarnings.map((w, i) => (
                <div key={i} style={{ display: 'flex', gap: 6 }}>
                  <Icon name="alert" size={13} style={{ marginTop: 1 }} />
                  <span>{w}</span>
                </div>
              ))}
            </div>
          )}
          {loadingPosts && posts.length === 0 ? (
            <div
              style={{
                color: C.basil,
                fontSize: 12.5,
                marginBottom: 20,
                fontWeight: 600,
                animation: 'basilpulse 1s ease-in-out infinite',
              }}
            >
              Loading your posts…
            </div>
          ) : posts.length === 0 ? (
            <EmptyState title="No posts found to amplify">
              Once your Facebook Page / Instagram has published posts (and the connection has the
              right permissions), they'll appear here.
            </EmptyState>
          ) : (
            <>
              {/* Platform filter tabs + winners filter + sort */}
              <div
                style={{
                  display: 'flex',
                  gap: 10,
                  marginBottom: 14,
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {(
                    [
                      { key: 'all', label: 'All', n: posts.length },
                      { key: 'facebook', label: 'Facebook', n: fbCount },
                      { key: 'instagram', label: 'Instagram', n: igCount },
                    ] as const
                  ).map((t) => {
                    const on = postPlatform === t.key;
                    return (
                      <button
                        key={t.key}
                        type="button"
                        onClick={() => {
                          setPostPlatform(t.key);
                          setPostLimit(POSTS_PAGE);
                        }}
                        style={{
                          padding: '6px 13px',
                          borderRadius: 8,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          fontSize: 12,
                          fontWeight: 600,
                          border: `1px solid ${on ? C.basil : C.line}`,
                          background: on ? alpha(C.basil, 12) : C.surface,
                          color: on ? C.basil : C.secondary,
                        }}
                      >
                        {t.label}{' '}
                        <span style={{ color: on ? C.basil : C.faint, fontWeight: 500 }}>
                          {t.n}
                        </span>
                      </button>
                    );
                  })}
                </div>

                <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                  {/* Winners-only filter */}
                  <button
                    type="button"
                    onClick={() => {
                      setWinnersOnly((v) => !v);
                      setPostLimit(POSTS_PAGE);
                    }}
                    title="Show only posts outperforming your median"
                    style={{
                      padding: '6px 12px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: 12,
                      fontWeight: 600,
                      border: `1px solid ${winnersOnly ? C.good : C.line}`,
                      background: winnersOnly ? alpha(C.good, 14) : C.surface,
                      color: winnersOnly ? C.good : C.secondary,
                    }}
                  >
                    <Icon
                      name="flame"
                      size={12}
                      style={{ display: 'inline-block', verticalAlign: '-2px', marginRight: 4 }}
                    />
                    Winners{' '}
                    <span style={{ color: winnersOnly ? C.good : C.faint, fontWeight: 500 }}>
                      {winnerCount}
                    </span>
                  </button>
                  {/* Sort: recent vs top-performing */}
                  <div
                    style={{
                      display: 'flex',
                      border: `1px solid ${C.line}`,
                      borderRadius: 8,
                      overflow: 'hidden',
                    }}
                  >
                    {(['recent', 'top'] as const).map((s) => {
                      const on = postSort === s;
                      return (
                        <button
                          key={s}
                          type="button"
                          onClick={() => {
                            setPostSort(s);
                            setPostLimit(POSTS_PAGE);
                          }}
                          style={{
                            padding: '6px 12px',
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            fontSize: 12,
                            fontWeight: 600,
                            border: 'none',
                            background: on ? alpha(C.basil, 12) : 'transparent',
                            color: on ? C.basil : C.muted,
                          }}
                        >
                          {s === 'recent' ? 'Recent' : 'Top'}
                        </button>
                      );
                    })}
                  </div>
                </div>
              </div>

              {filteredPosts.length === 0 ? (
                <div style={{ color: C.muted, fontSize: 12.5, marginBottom: 26 }}>
                  {winnersOnly
                    ? 'No standout posts yet — none are beating your median. Check back as posts gather engagement.'
                    : `No ${postPlatform === 'all' ? '' : postPlatform + ' '}posts found.`}
                </div>
              ) : (
                <>
                  {/* The badge names WHICH COPY of the post gets amplified, not where
                      the ad will show. Read as "only runs on Facebook" it stops people
                      boosting a perfectly good post, so say it plainly, once. */}
                  <div
                    style={{
                      fontSize: 12,
                      color: C.muted,
                      lineHeight: 1.5,
                      marginBottom: 12,
                    }}
                  >
                    The <b style={{ color: C.secondary }}>FACEBOOK</b> /{' '}
                    <b style={{ color: C.secondary }}>INSTAGRAM</b> badge shows which copy of the
                    post the ad is built from. Either way the ad runs on both Facebook and
                    Instagram, Stories and Reels included. A post marked{' '}
                    <b style={{ color: C.secondary }}>both</b> was found on each platform, so you
                    can choose which copy keeps its likes and comments.
                  </div>
                  <div
                    style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(3,1fr)',
                      gap: 12,
                      marginBottom: 14,
                    }}
                  >
                    {visibleGroups.map((g) => {
                      const lead = g.find((p) => p.thumbnail) ?? g[0]!;
                      const paired = g.length > 1;
                      const active = g.some((p) => p.id === selectedPostId);
                      const winner = g.some((p) => p.outperforming);
                      const boostLive = g.some((p) => p.boostState === 'live');
                      // Meta refuses to promote at least one side of this post and
                      // there is no way around it, so don't let it be picked — a
                      // build against it can only fail (licensed music, usually).
                      const blocked = g.every((p) => p.boostable === false);
                      const blockedWhy = g.find((p) => p.boostBlockedReason)?.boostBlockedReason;
                      const reach = g.reduce((s, p) => s + (p.reach ?? 0), 0);
                      const eng = g.reduce((s, p) => s + (p.engagement ?? 0), 0);
                      const hasReach = g.some((p) => p.reach != null);
                      const hasEng = g.some((p) => p.engagement != null);
                      return (
                        <div
                          key={g.map((p) => p.id).join('+')}
                          onClick={() => !blocked && setSelectedPostId(lead.id)}
                          title={blocked ? (blockedWhy ?? undefined) : undefined}
                          style={{
                            cursor: blocked ? 'not-allowed' : 'pointer',
                            borderRadius: 12,
                            overflow: 'hidden',
                            background: C.surface,
                            border: `1.5px solid ${active ? C.basil : C.line}`,
                            opacity: blocked ? 0.55 : 1,
                          }}
                        >
                          {lead.thumbnail && (
                            <img
                              src={lead.thumbnail}
                              alt=""
                              // Meta CDN thumbnail URLs are time-limited; if a cached
                              // one has expired, hide it rather than show a broken icon.
                              onError={(e) => (e.currentTarget.style.display = 'none')}
                              style={{
                                width: '100%',
                                height: 110,
                                objectFit: 'cover',
                                display: 'block',
                              }}
                            />
                          )}
                          <div style={{ padding: '10px 12px' }}>
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                marginBottom: 4,
                                flexWrap: 'wrap',
                              }}
                            >
                              {g.map((p) => {
                                const sel = p.id === selectedPostId;
                                return (
                                  <span
                                    key={p.id}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (p.boostable !== false) setSelectedPostId(p.id);
                                    }}
                                    title={
                                      p.boostable === false
                                        ? (p.boostBlockedReason ?? undefined)
                                        : paired
                                          ? `Amplify the ${p.platform} copy of this post`
                                          : undefined
                                    }
                                    style={{
                                      font: `600 9px 'IBM Plex Mono',monospace`,
                                      color: p.platform === 'instagram' ? C.magic : C.info,
                                      textTransform: 'uppercase',
                                      cursor: paired ? 'pointer' : 'default',
                                      borderBottom:
                                        paired && sel
                                          ? `1.5px solid ${p.platform === 'instagram' ? C.magic : C.info}`
                                          : '1.5px solid transparent',
                                    }}
                                  >
                                    {p.platform}
                                  </span>
                                );
                              })}
                              {paired && (
                                <span
                                  title="Cross-posted to both Facebook and Instagram"
                                  style={{
                                    font: `500 9px 'IBM Plex Mono',monospace`,
                                    color: C.faint,
                                  }}
                                >
                                  · both
                                </span>
                              )}
                              {winner && (
                                <span
                                  title="Engagement well above your median — a strong post to promote"
                                  style={{
                                    font: `700 9px 'IBM Plex Mono',monospace`,
                                    color: C.good,
                                    textTransform: 'uppercase',
                                    letterSpacing: '.03em',
                                  }}
                                >
                                  <Icon
                                    name="flame"
                                    size={10}
                                    style={{
                                      display: 'inline-block',
                                      verticalAlign: '-1px',
                                      marginRight: 3,
                                    }}
                                  />
                                  Winner
                                </span>
                              )}
                              {blocked && (
                                <span
                                  title={blockedWhy ?? undefined}
                                  style={{
                                    font: `700 9px 'IBM Plex Mono',monospace`,
                                    color: C.watch,
                                    textTransform: 'uppercase',
                                    letterSpacing: '.03em',
                                  }}
                                >
                                  Can&apos;t be boosted
                                </span>
                              )}
                            </div>
                            {blocked && blockedWhy && (
                              <div
                                style={{
                                  fontSize: 11,
                                  color: C.watch,
                                  lineHeight: 1.45,
                                  marginBottom: 6,
                                }}
                              >
                                {blockedWhy}
                              </div>
                            )}
                            <div
                              style={{
                                fontSize: 12.5,
                                color: C.secondary,
                                lineHeight: 1.4,
                                maxHeight: 52,
                                overflow: 'hidden',
                              }}
                            >
                              {lead.caption || '(no caption)'}
                            </div>
                            {(hasReach || hasEng || boostLive) && (
                              <div
                                style={{
                                  font: `500 10px 'IBM Plex Mono',monospace`,
                                  color: C.faint,
                                  marginTop: 7,
                                  display: 'flex',
                                  gap: 10,
                                  flexWrap: 'wrap',
                                  alignItems: 'center',
                                }}
                              >
                                {hasReach && (
                                  <span>
                                    {fmtNum(reach)} reach{paired ? ' (both)' : ''}
                                  </span>
                                )}
                                {hasEng && <span>{fmtNum(eng)} eng</span>}
                                {boostLive && (
                                  <span
                                    title="A live ad is currently promoting this post"
                                    style={{
                                      color: C.basil,
                                      marginLeft: 'auto',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      gap: 3,
                                    }}
                                  >
                                    <Icon name="boost" size={11} />
                                    running
                                  </span>
                                )}
                              </div>
                            )}
                            <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
                              {g
                                .filter((p) => p.permalink)
                                .map((p) => (
                                  <a
                                    key={p.id}
                                    href={p.permalink!}
                                    target="_blank"
                                    rel="noreferrer"
                                    onClick={(e) => e.stopPropagation()}
                                    style={{
                                      fontSize: 11.5,
                                      color: C.basil,
                                      display: 'inline-block',
                                    }}
                                  >
                                    View {paired ? p.platform : 'live'} →
                                  </a>
                                ))}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  {postGroups.length > postLimit && (
                    <div style={{ marginBottom: 26 }}>
                      <button
                        type="button"
                        onClick={() => setPostLimit((n) => n + POSTS_PAGE)}
                        style={{
                          padding: '9px 16px',
                          borderRadius: 9,
                          cursor: 'pointer',
                          fontFamily: 'inherit',
                          fontSize: 12.5,
                          fontWeight: 600,
                          border: `1px solid ${C.line}`,
                          background: C.surface,
                          color: C.secondary,
                        }}
                      >
                        Show more ({postGroups.length - postLimit} earlier)
                      </button>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </>
      )}

      {mode === 'partnership' && (
        <>
          <div className="label-mono" style={{ margin: '6px 0 10px' }}>
            1 · Paste the creator&rsquo;s partnership ad code
          </div>
          <div
            style={{
              fontSize: 12.5,
              color: C.muted,
              lineHeight: 1.65,
              margin: '0 0 14px',
              maxWidth: 620,
            }}
          >
            A collab or paid-partnership post belongs to the creator, not to you, so it never shows
            up in the boost picker above. The code is how they hand you permission to run it. Ask
            them to open the post, then <b>&hellip; → Partnership ads</b>, and send you the code.
            Basil runs <i>their actual post</i>, so its likes, comments and shares stay on the ad
            and keep building, and their caption runs with it. There is no ad copy to write.
          </div>
          <Card style={{ padding: 22, marginBottom: 22 }}>
            <Row label="Partnership ad code">
              <div>
                <input
                  value={partnershipCode}
                  onChange={(e) => setPartnershipCode(e.target.value)}
                  placeholder="adcode-…"
                  spellCheck={false}
                  autoCapitalize="off"
                  autoCorrect="off"
                  style={{ ...inputStyle, fontFamily: 'ui-monospace, monospace', fontSize: 12 }}
                />
                <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
                  Paste it rather than retyping — the codes are case-sensitive, and one wrong
                  character just fails. Each code is tied to a limited number of ad accounts and
                  expires, so if Meta rejects it, ask the creator for a fresh one.
                </div>
              </div>
            </Row>
          </Card>
          <div
            style={{
              fontSize: 11.5,
              color: C.muted,
              lineHeight: 1.6,
              margin: '0 0 22px',
              maxWidth: 620,
            }}
          >
            Worth asking once: if the creator adds your business under{' '}
            <b>Branded content → brand partners</b>, future collabs become promotable without
            chasing a new code each time.
          </div>
        </>
      )}

      {mode === 'organic' && (
        <>
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 10,
              flexWrap: 'wrap',
              margin: '6px 0 4px',
            }}
          >
            <div className="label-mono">1 · Pick a post to publish</div>
            <span
              onClick={() => navigate('/studio/library')}
              style={{ fontSize: 11.5, color: C.basil, cursor: 'pointer', fontWeight: 600 }}
            >
              Manage all posts in Studio → Library →
            </span>
          </div>
          <div style={{ fontSize: 12, color: C.faint, marginBottom: 12 }}>
            Publish a saved post to Facebook / Instagram for free (no ad spend). Let it prove itself
            organically, then boost the winners into an ad from the Amplify tab.
          </div>
          {creatives.length === 0 ? (
            <EmptyState title="No posts to publish yet">
              Make a post in Studio → Compose (pick a visual + write the copy, Save post). Then come
              back to publish it.
            </EmptyState>
          ) : (
            <>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3,1fr)',
                  gap: 12,
                  marginBottom: 22,
                }}
              >
                {creatives.map((cr) => {
                  const active = cr.id === pubCreativeId;
                  const url = cr.media_id ? mediaUrls[cr.media_id] : undefined;
                  return (
                    <div
                      key={cr.id}
                      onClick={() => {
                        setPubCreativeId(cr.id);
                        setPubResult(null);
                        setConfirmPublish(false);
                      }}
                      style={{
                        cursor: 'pointer',
                        borderRadius: 12,
                        overflow: 'hidden',
                        background: C.surface,
                        border: `1.5px solid ${active ? C.basil : C.line}`,
                      }}
                    >
                      {url && (
                        <img
                          src={url}
                          alt=""
                          style={{
                            width: '100%',
                            height: 110,
                            objectFit: 'cover',
                            display: 'block',
                          }}
                        />
                      )}
                      <div style={{ padding: '10px 12px' }}>
                        <div
                          style={{
                            fontSize: 12.5,
                            color: C.secondary,
                            lineHeight: 1.4,
                            maxHeight: 52,
                            overflow: 'hidden',
                          }}
                        >
                          {cr.primary_text?.slice(0, 90) || '(no caption)'}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              <div className="label-mono" style={{ marginBottom: 8 }}>
                2 · Where to publish
              </div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 18, flexWrap: 'wrap' }}>
                {(
                  [
                    { key: 'fb', label: 'Facebook', on: pubFB, set: setPubFB },
                    { key: 'ig', label: 'Instagram', on: pubIG, set: setPubIG },
                  ] as const
                ).map((p) => (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => p.set((v) => !v)}
                    style={{
                      padding: '7px 14px',
                      borderRadius: 8,
                      cursor: 'pointer',
                      fontFamily: 'inherit',
                      fontSize: 12.5,
                      fontWeight: 600,
                      border: `1px solid ${p.on ? C.basil : C.line}`,
                      background: p.on ? alpha(C.basil, 12) : C.surface,
                      color: p.on ? C.basil : C.muted,
                    }}
                  >
                    {p.on ? '✓ ' : ''}
                    {p.label}
                  </button>
                ))}
              </div>

              {pubResult && (
                <div style={{ marginBottom: 16, fontSize: 12.5, lineHeight: 1.7 }}>
                  {pubResult.published.map((r) => (
                    <div key={r.platform} style={{ color: C.good }}>
                      ✓ Published to {r.platform}
                      {r.permalink && (
                        <>
                          {' · '}
                          <a
                            href={r.permalink}
                            target="_blank"
                            rel="noreferrer"
                            style={{ color: C.basil }}
                          >
                            View live →
                          </a>
                        </>
                      )}
                    </div>
                  ))}
                  {pubResult.errors.map((e, i) => (
                    <div
                      key={i}
                      style={{ color: C.watch, display: 'flex', gap: 6, alignItems: 'baseline' }}
                    >
                      <Icon name="alert" size={12} />
                      <span>
                        {e.platform}: {e.message}
                      </span>
                    </div>
                  ))}
                </div>
              )}

              {confirmPublish ? (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '12px 16px',
                    borderRadius: 10,
                    background: alpha(C.basil, 8),
                    border: `1px solid ${alpha(C.basil, 30)}`,
                  }}
                >
                  <span style={{ fontSize: 12.5, color: C.secondary }}>
                    Publish this post publicly to{' '}
                    {[pubFB && 'Facebook', pubIG && 'Instagram'].filter(Boolean).join(' & ')} now?
                  </span>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                    <Button
                      onClick={() => void publishOrganicPost()}
                      style={{ background: C.basil, color: C.basilInk, borderColor: C.basil }}
                    >
                      {publishing ? 'Publishing…' : 'Confirm & publish'}
                    </Button>
                    <Button onClick={() => setConfirmPublish(false)}>Cancel</Button>
                  </div>
                </div>
              ) : (
                <Button
                  onClick={() => setConfirmPublish(true)}
                  disabled={!pubCreativeId || (!pubFB && !pubIG)}
                  style={{
                    background: C.basil,
                    color: C.basilInk,
                    borderColor: C.basil,
                    opacity: !pubCreativeId || (!pubFB && !pubIG) ? 0.5 : 1,
                  }}
                >
                  Publish organically →
                </Button>
              )}
              <div style={{ fontSize: 11, color: C.faint, marginTop: 10 }}>
                Free, no ad spend. Posts publish to your Page / Instagram immediately. Video and
                Reels can take a minute to process.
              </div>
            </>
          )}
        </>
      )}

      {mode === 'new' && (
        <>
          {/* 1. Pick a post */}
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 10,
              flexWrap: 'wrap',
              margin: '6px 0 4px',
            }}
          >
            <div className="label-mono">1 · Pick a post to launch</div>
            <span
              onClick={() => navigate('/studio/library')}
              style={{ fontSize: 11.5, color: C.basil, cursor: 'pointer', fontWeight: 600 }}
            >
              Manage all posts in Studio → Library →
            </span>
          </div>
          <div style={{ fontSize: 12, color: C.faint, marginBottom: 12 }}>
            These are your saved posts (from Copy) that have an image attached. Edit the caption or
            delete one below, or manage everything (image, folders, carousel) in Posts.
          </div>
          {creatives.length === 0 ? (
            <EmptyState title="No launchable posts yet">
              Make a post in Studio → Compose (pick a visual + write the copy, Save post), then come
              back to launch it — or use "⇢ Send to Launch →" straight from Compose.
            </EmptyState>
          ) : (
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3,1fr)',
                gap: 12,
                marginBottom: 26,
              }}
            >
              {creatives.map((cr) => {
                const active = cr.id === creativeId;
                const url = cr.media_id ? mediaUrls[cr.media_id] : undefined;
                return (
                  <div
                    key={cr.id}
                    onClick={() => editId !== cr.id && setCreativeId(cr.id)}
                    style={{
                      cursor: 'pointer',
                      borderRadius: 12,
                      overflow: 'hidden',
                      background: C.surface,
                      border: `1.5px solid ${active ? C.basil : C.line}`,
                    }}
                  >
                    {url && (
                      <img
                        src={url}
                        alt=""
                        style={{ width: '100%', height: 110, objectFit: 'cover', display: 'block' }}
                      />
                    )}
                    <div style={{ padding: '10px 12px' }}>
                      <div
                        style={{
                          font: `500 9px 'IBM Plex Mono',monospace`,
                          color: C.faint,
                          marginBottom: 4,
                        }}
                      >
                        {cr.desire?.toUpperCase()} · {cr.hook_pattern}
                      </div>
                      {editId === cr.id ? (
                        <div onClick={(e) => e.stopPropagation()}>
                          <textarea
                            autoFocus
                            value={draft}
                            onChange={(e) => setDraft(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Escape') setEditId(null);
                            }}
                            rows={4}
                            style={{
                              width: '100%',
                              boxSizing: 'border-box',
                              background: C.surface2,
                              border: `1px solid ${C.basil}`,
                              borderRadius: 8,
                              padding: '7px 9px',
                              fontSize: 12.5,
                              color: C.primary,
                              fontFamily: 'inherit',
                              lineHeight: 1.4,
                              resize: 'vertical',
                            }}
                          />
                          <div style={{ display: 'flex', gap: 12, marginTop: 6, fontSize: 11.5 }}>
                            <span
                              onClick={() => void saveCaption(cr.id)}
                              style={{ color: C.basil, fontWeight: 600, cursor: 'pointer' }}
                            >
                              Save
                            </span>
                            <span
                              onClick={() => setEditId(null)}
                              style={{ color: C.muted, cursor: 'pointer' }}
                            >
                              Cancel
                            </span>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div
                            style={{
                              fontSize: 12.5,
                              color: C.secondary,
                              lineHeight: 1.4,
                              maxHeight: 52,
                              overflow: 'hidden',
                            }}
                          >
                            {cr.primary_text}
                          </div>
                          <div
                            style={{
                              display: 'flex',
                              gap: 12,
                              marginTop: 8,
                              fontSize: 11.5,
                              alignItems: 'center',
                            }}
                          >
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditId(cr.id);
                                setDraft(cr.primary_text);
                              }}
                              style={{ color: C.basil, cursor: 'pointer', fontWeight: 600 }}
                            >
                              ✎ Edit
                            </span>
                            <span
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/studio/create?creative=${cr.id}`);
                              }}
                              style={{ color: C.secondary, cursor: 'pointer' }}
                            >
                              Image
                            </span>
                            {confirmDelCr === cr.id ? (
                              <span style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    void deleteCreative(cr.id);
                                  }}
                                  style={{ color: C.act, fontWeight: 600, cursor: 'pointer' }}
                                >
                                  Delete?
                                </span>
                                <span
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setConfirmDelCr(null);
                                  }}
                                  style={{ color: C.muted, cursor: 'pointer' }}
                                >
                                  No
                                </span>
                              </span>
                            ) : (
                              <span
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setConfirmDelCr(cr.id);
                                }}
                                style={{ marginLeft: 'auto', color: C.act, cursor: 'pointer' }}
                              >
                                Delete
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* 2. Configure — ad targeting/budget; not shown for organic publishing */}
      {mode !== 'organic' && (
        <>
          <div className="label-mono" style={{ margin: '6px 0 12px' }}>
            2 · Configure
          </div>
          <Card style={{ padding: 22, marginBottom: 22 }}>
            <Row label="Campaign name">
              <div>
                <input
                  value={name}
                  onChange={(e) => {
                    nameTouched.current = true;
                    setName(e.target.value);
                  }}
                  placeholder={suggestedName || 'Name this campaign'}
                  maxLength={120}
                  style={inputStyle}
                />
                <div style={{ fontSize: 11.5, color: C.faint, marginTop: 6, lineHeight: 1.5 }}>
                  How it shows in Meta Ads Manager and across Basil. Leave blank and Basil names it
                  for you.
                </div>
              </div>
            </Row>
            <Row label="Publish as (Facebook Page)">
              {pages.length > 0 ? (
                <select
                  value={pageId}
                  onChange={(e) => setPageId(e.target.value)}
                  style={selectStyle}
                >
                  {pages.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name} {p.username ? `(@${p.username})` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <div>
                  <input
                    value={pageId}
                    onChange={(e) => setPageId(e.target.value.trim())}
                    placeholder="Paste your Facebook Page ID (e.g. 1029384756)"
                    style={inputStyle}
                  />
                  <div
                    style={{
                      fontSize: 11.5,
                      color: C.faint,
                      marginTop: 6,
                      lineHeight: 1.5,
                      display: 'flex',
                      justifyContent: 'space-between',
                      gap: 12,
                    }}
                  >
                    <span>
                      Find the ID in Business Settings → Accounts → Pages → Phuket Cleanse. Save it
                      once and it auto-fills.
                    </span>
                    {pageId.trim() && (
                      <span
                        onClick={() => void saveDefaultPage()}
                        style={{ color: C.basil, cursor: 'pointer', whiteSpace: 'nowrap' }}
                      >
                        ★ Save as default
                      </span>
                    )}
                  </div>
                </div>
              )}
            </Row>
            <Row label="Destination link">
              <div>
                <input
                  value={link}
                  onChange={(e) => setLink(e.target.value)}
                  placeholder={tenantSite(activeOrg?.slug) ?? 'https://'}
                  style={inputStyle}
                />
                {(mode === 'amplify' || mode === 'partnership') && (
                  <div style={{ fontSize: 11.5, color: C.muted, marginTop: 6, lineHeight: 1.5 }}>
                    Where the button below sends people. Meta requires a destination for a paid
                    post, so this is prefilled with {activeOrg?.name ?? 'this workspace'}&rsquo;s
                    own site — point it at the matching blog or landing page for cleaner
                    attribution.
                  </div>
                )}
              </div>
            </Row>
            <Row label="Button">
              <div>
                <select
                  value={ctaType}
                  onChange={(e) => setCtaType(e.target.value)}
                  style={{ ...selectStyle, width: 220 }}
                >
                  {CTA_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {ctaType !== 'LEARN_MORE' && (
                  <div style={{ fontSize: 11.5, color: C.watch, marginTop: 6, lineHeight: 1.5 }}>
                    Heads up: the brand paradigm favors “Learn More” (a soft, transitional CTA to
                    the blog). Stronger buttons like “Book Now” push harder than the house style.
                  </div>
                )}
              </div>
            </Row>
            <Row label="Audience">
              <div>
                <select
                  value={audienceSel}
                  onChange={(e) => setAudienceSel(e.target.value)}
                  style={selectStyle}
                >
                  <option value="">Standard — age 40+, your cities (locked)</option>
                  {buckets.length > 0 && (
                    <optgroup label="Basil audiences">
                      {buckets.map((bk) => (
                        <option key={bk.id} value={`b:${bk.id}`}>
                          {bk.name}
                        </option>
                      ))}
                    </optgroup>
                  )}
                  {metaAudiences.length > 0 && (
                    <optgroup label="Your Meta audiences (custom + lookalike)">
                      {metaAudiences.map((a) => (
                        <option key={a.id} value={`m:${a.id}`}>
                          {a.name}
                          {a.subtype === 'LOOKALIKE' ? ' · lookalike' : ''}
                          {a.country ? ` · ${codeToName(a.country)}` : ''}
                          {a.size != null ? ` · ${a.size.toLocaleString()}` : ''}
                        </option>
                      ))}
                    </optgroup>
                  )}
                </select>
                <div
                  style={{
                    fontSize: 11.5,
                    color: C.faint,
                    marginTop: 6,
                    lineHeight: 1.5,
                    display: 'flex',
                    justifyContent: 'space-between',
                    gap: 12,
                  }}
                >
                  <span>
                    {activeBucket
                      ? 'This saved audience replaces the standard targeting for this campaign.'
                      : metaAudId
                        ? 'Targeting this Meta audience — add the cities to run it in below.'
                        : 'The locked Targeting Standard (RULES §3). Pick a saved audience to target beyond it.'}
                  </span>
                  <span
                    onClick={() => navigate('/settings')}
                    style={{ color: C.basil, cursor: 'pointer', whiteSpace: 'nowrap' }}
                  >
                    Manage audiences →
                  </span>
                </div>
                {activeBucket && (
                  <div
                    style={{
                      marginTop: 10,
                      padding: '10px 12px',
                      border: `1px solid ${C.line}`,
                      borderRadius: 9,
                      background: C.surface2,
                    }}
                  >
                    <AudienceSummary spec={activeBucket.spec} />
                  </div>
                )}
              </div>
            </Row>
            {!activeBucket &&
              (useWholeCountry ? (
                <Row label="Target">
                  <div>
                    <span
                      style={{
                        ...chipStyle,
                        background: alpha(C.basil, 12),
                        color: C.basil,
                        fontWeight: 600,
                      }}
                    >
                      🌐 All of {codeToName(metaCountry!)}
                    </span>
                    <div style={{ fontSize: 11.5, color: C.faint, marginTop: 8, lineHeight: 1.5 }}>
                      Targeting the whole country this lookalike was built for — the audience is the
                      precision.{' '}
                      <span
                        onClick={() => setWholeCountry(false)}
                        style={{ color: C.basil, cursor: 'pointer', fontWeight: 600 }}
                      >
                        Target specific cities instead
                      </span>
                    </div>
                  </div>
                </Row>
              ) : (
                <Row label="Target cities (live from Meta)">
                  <div>
                    {metaCountry && (
                      <div style={{ fontSize: 11.5, color: C.faint, marginBottom: 8 }}>
                        <span
                          onClick={() => setWholeCountry(true)}
                          style={{ color: C.basil, cursor: 'pointer', fontWeight: 600 }}
                        >
                          ← Target all of {codeToName(metaCountry)}
                        </span>{' '}
                        instead of specific cities
                      </div>
                    )}
                    <div style={{ position: 'relative', marginBottom: cities.length ? 10 : 0 }}>
                      <input
                        value={geoQuery}
                        onChange={(e) => setGeoQuery(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'ArrowDown') {
                            e.preventDefault();
                            setHighlight((h) => Math.min(h + 1, geoResults.length - 1));
                          } else if (e.key === 'ArrowUp') {
                            e.preventDefault();
                            setHighlight((h) => Math.max(h - 1, 0));
                          } else if (e.key === 'Enter') {
                            e.preventDefault();
                            const g = geoResults[highlight];
                            if (g) addCity(g);
                          } else if (e.key === 'Escape') {
                            setGeoResults([]);
                          }
                        }}
                        placeholder="Type a city, e.g. Sydney"
                        autoComplete="off"
                        style={{ ...inputStyle, width: '100%' }}
                      />
                      {geoQuery.trim().length >= 2 && (
                        <div
                          style={{
                            position: 'absolute',
                            top: 'calc(100% + 4px)',
                            left: 0,
                            right: 0,
                            zIndex: 30,
                            background: C.surface,
                            border: `1px solid ${C.line}`,
                            borderRadius: 9,
                            overflow: 'hidden',
                            maxHeight: 240,
                            overflowY: 'auto',
                            boxShadow: '0 12px 32px rgba(0,0,0,.4)',
                          }}
                        >
                          {geoResults.length === 0 && searching ? (
                            <div
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 8,
                                padding: '9px 12px',
                                color: C.muted,
                                fontSize: 12.5,
                              }}
                            >
                              <span
                                style={{
                                  width: 11,
                                  height: 11,
                                  border: `2px solid ${alpha(C.basil, 30)}`,
                                  borderTopColor: C.basil,
                                  borderRadius: 11,
                                  display: 'inline-block',
                                  animation: 'basilspin .7s linear infinite',
                                }}
                              />
                              Searching…
                            </div>
                          ) : geoResults.length === 0 ? (
                            <div style={{ padding: '9px 12px', color: C.muted, fontSize: 12.5 }}>
                              No places match “{geoQuery.trim()}”. Try a city or region name.
                            </div>
                          ) : (
                            geoResults.map((g, i) => (
                              <div
                                key={g.key}
                                onMouseDown={(e) => {
                                  e.preventDefault();
                                  addCity(g);
                                }}
                                onMouseEnter={() => setHighlight(i)}
                                style={{
                                  padding: '9px 12px',
                                  cursor: 'pointer',
                                  fontSize: 13,
                                  color: C.primary,
                                  background: i === highlight ? C.surface2 : 'transparent',
                                }}
                              >
                                {g.name}
                                {g.region ? `, ${g.region}` : ''}
                                {g.country_name ? (
                                  <span style={{ color: C.faint }}> · {g.country_name}</span>
                                ) : null}
                              </div>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                    <div
                      style={{
                        font: `500 10px 'IBM Plex Mono',monospace`,
                        color: C.faint,
                        marginBottom: 8,
                      }}
                    >
                      ↑↓ TO CHOOSE · ENTER TO ADD
                    </div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                      {cities.map((c) => (
                        <span
                          key={c.key}
                          style={{ ...chipStyle, background: alpha(C.basil, 12), color: C.basil }}
                        >
                          {c.name}
                          <span
                            onClick={() => setCities((cs) => cs.filter((x) => x.key !== c.key))}
                            style={{ marginLeft: 6, cursor: 'pointer', opacity: 0.7 }}
                          >
                            ✕
                          </span>
                        </span>
                      ))}
                    </div>
                  </div>
                </Row>
              ))}
            <Row label={`Daily budget (${currencySymbol(activeOrg?.default_currency)})`}>
              <div>
                <input
                  type="number"
                  value={budget}
                  onChange={(e) => setBudget(Number(e.target.value))}
                  style={{ ...inputStyle, width: 140 }}
                />
                {overCeiling && (
                  <label
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      marginTop: 10,
                      color: C.watch,
                      fontSize: 12.5,
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={override}
                      onChange={(e) => setOverride(e.target.checked)}
                    />
                    Over the {money(dailyCeiling)}/day ceiling — I confirm this override.
                  </label>
                )}
              </div>
            </Row>
            <Row label="Start date (optional)">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <input
                  type="date"
                  value={startDate}
                  min={new Date().toISOString().slice(0, 10)}
                  onChange={(e) => setStartDate(e.target.value)}
                  style={{ ...inputStyle, width: 180 }}
                />
                {startDate && (
                  <span
                    onClick={() => setStartDate('')}
                    style={{
                      font: `600 11px 'IBM Plex Mono',monospace`,
                      color: C.basil,
                      cursor: 'pointer',
                    }}
                  >
                    Clear
                  </span>
                )}
                <span style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>
                  {startDate
                    ? 'Scheduled to begin this day (your account timezone). Still build now + Go Live to arm it.'
                    : 'Leave blank to begin as soon as you Go Live.'}
                </span>
              </div>
            </Row>
            <Row label="End date (optional)">
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
                <input
                  type="date"
                  value={endDate}
                  min={
                    startDate
                      ? new Date(new Date(startDate).getTime() + 86400000)
                          .toISOString()
                          .slice(0, 10)
                      : new Date(Date.now() + 86400000).toISOString().slice(0, 10)
                  }
                  onChange={(e) => setEndDate(e.target.value)}
                  style={{ ...inputStyle, width: 180 }}
                />
                {endDate && (
                  <span
                    onClick={() => setEndDate('')}
                    style={{
                      font: `600 11px 'IBM Plex Mono',monospace`,
                      color: C.basil,
                      cursor: 'pointer',
                    }}
                  >
                    Clear
                  </span>
                )}
                <span style={{ fontSize: 11.5, color: C.muted, lineHeight: 1.5 }}>
                  {endDate
                    ? 'Delivery stops at the end of this day (your account timezone).'
                    : 'Leave blank to run until you pause it.'}
                </span>
              </div>
            </Row>
            <div
              style={{
                font: `500 11px 'IBM Plex Mono',monospace`,
                color: C.faint,
                marginTop: 6,
                lineHeight: 1.6,
              }}
            >
              LOCKED: Objective Traffic ·{' '}
              {activeBucket ? 'audience from your saved bucket' : 'Age 40+ · your cities'} ·
              Facebook + Instagram only · Audience Network OFF · no expansion · CBO on. Built PAUSED
              — nothing spends until you Go Live.
            </div>
            <Button
              variant="primary"
              onClick={() => canBuild && !building && void build()}
              style={{
                marginTop: 18,
                padding: '11px 20px',
                fontSize: 13,
                opacity: canBuild && !building ? 1 : 0.5,
                cursor: canBuild && !building ? 'pointer' : 'default',
              }}
            >
              {building ? (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                  <Spinner size={13} color={C.basilInk} /> Building…
                </span>
              ) : mode === 'amplify' ? (
                'Amplify post (paused)'
              ) : mode === 'partnership' ? (
                'Build partnership ad (paused)'
              ) : (
                'Build campaign (paused)'
              )}
            </Button>
            <div style={{ marginTop: 10, fontSize: 11, color: C.faint }}>
              Your setup here is auto-saved as a draft, so you can leave and come back without
              losing it.
            </div>
          </Card>
        </>
      )}

      {msg && (
        <div
          style={{
            marginBottom: 22,
            padding: '10px 14px',
            borderRadius: 10,
            background: alpha(C.good, 10),
            border: `1px solid ${alpha(C.good, 25)}`,
            font: `500 12px 'IBM Plex Mono',monospace`,
            color: C.secondary,
          }}
        >
          {msg}
        </div>
      )}

      {/* 3. Your campaigns — ad campaigns only; hidden on the free organic tab */}
      {mode !== 'organic' && (
        <>
          <div className="label-mono" style={{ margin: '6px 0 12px' }}>
            3 · Your campaigns
          </div>
          {roas && roas.total_spend > 0 && (
            <Card
              style={{
                padding: '14px 18px',
                marginBottom: 12,
                display: 'flex',
                gap: 26,
                flexWrap: 'wrap',
              }}
            >
              <Stat label="SPEND" value={money(roas.total_spend)} />
              <Stat label="ATTRIBUTED REVENUE" value={money(roas.attributed_revenue)} />
              <Stat
                label="BLENDED ROAS"
                value={
                  roas.total_spend > 0
                    ? `${(roas.attributed_revenue / roas.total_spend).toFixed(2)}x`
                    : '—'
                }
              />
              <Stat
                label="ATTRIBUTION"
                value={roas.coverage != null ? `${Math.round(roas.coverage * 100)}%` : '—'}
                hint="of booking revenue tied to a campaign — the rest (WhatsApp, direct) stays honestly unattributed"
              />
            </Card>
          )}
          {launches.length === 0 ? (
            <EmptyState title="No campaigns built yet">
              Build one above — it lands in Meta paused, and you take it live from here.
            </EmptyState>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {launches.map((l) => (
                <Card
                  key={l.id}
                  style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 14 }}
                >
                  <StatusDot status={l.status} />
                  {(() => {
                    const cr = l.creative_id ? creativeById.get(l.creative_id) : undefined;
                    const url = cr?.media_id ? mediaUrls[cr.media_id] : undefined;
                    return url ? (
                      <img
                        src={url}
                        alt=""
                        style={{
                          width: 46,
                          height: 46,
                          objectFit: 'cover',
                          borderRadius: 9,
                          border: `1px solid ${C.line}`,
                          flexShrink: 0,
                        }}
                      />
                    ) : null;
                  })()}
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 13.5,
                        fontWeight: 600,
                        color: C.primary,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {launchName(l)}
                    </div>
                    <div
                      style={{
                        font: `500 10.5px 'IBM Plex Mono',monospace`,
                        color: C.faint,
                        marginTop: 3,
                      }}
                    >
                      {l.status.toUpperCase()} · {money(l.daily_budget ?? 0)}/day
                      {l.error ? ` · ${l.error}` : ''}
                    </div>
                  </div>
                  {(() => {
                    const r = l.meta_campaign_id ? roasBy.get(l.meta_campaign_id) : undefined;
                    if (!r || r.spend === 0) return null;
                    return (
                      <div style={{ display: 'flex', gap: 18, marginRight: 6 }}>
                        <Stat label="SPEND" value={money(r.spend)} small />
                        <Stat label="REVENUE" value={money(r.revenue)} small />
                        <Stat
                          label="ROAS"
                          value={r.roas != null ? `${r.roas.toFixed(2)}x` : '—'}
                          small
                          color={r.roas != null && r.roas >= 1 ? C.good : C.secondary}
                        />
                      </div>
                    );
                  })()}
                  {l.status === 'paused' &&
                    (busyLaunch === l.id ? (
                      <span
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 8,
                          fontSize: 12,
                          fontWeight: 600,
                          color: C.act,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        <Spinner size={13} color={C.act} /> Going live…
                      </span>
                    ) : confirmLaunch === l.id ? (
                      <div style={{ display: 'flex', gap: 6 }}>
                        <Button
                          variant="primary"
                          onClick={() => void goLive(l.id)}
                          style={{
                            padding: '7px 13px',
                            fontSize: 12,
                            background: C.act,
                            color: '#fff',
                          }}
                        >
                          Confirm spend
                        </Button>
                        <Button
                          onClick={() => setConfirmLaunch(null)}
                          style={{ padding: '7px 12px', fontSize: 12 }}
                        >
                          Cancel
                        </Button>
                      </div>
                    ) : (
                      <Button
                        onClick={() => setConfirmLaunch(l.id)}
                        style={{ padding: '7px 14px', fontSize: 12 }}
                      >
                        Go Live →
                      </Button>
                    ))}
                  {l.status === 'live' &&
                    l.meta_campaign_id &&
                    (busyLaunch === l.id ? (
                      <span
                        style={{
                          fontSize: 11.5,
                          color: C.faint,
                          fontWeight: 600,
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Pausing…
                      </span>
                    ) : confirmPause === l.id ? (
                      <span
                        style={{ fontSize: 11.5, display: 'flex', gap: 8, whiteSpace: 'nowrap' }}
                      >
                        <span
                          onClick={() => void pauseCampaign(l.id, l.meta_campaign_id!)}
                          style={{ color: C.watch, fontWeight: 600, cursor: 'pointer' }}
                        >
                          Pause spend?
                        </span>
                        <span
                          onClick={() => setConfirmPause(null)}
                          style={{ color: C.muted, cursor: 'pointer' }}
                        >
                          Cancel
                        </span>
                      </span>
                    ) : (
                      <span
                        onClick={() => {
                          setConfirmPause(l.id);
                          setConfirmDel(null);
                        }}
                        title="Pause this campaign in Meta (stops spend)"
                        style={{
                          fontSize: 11.5,
                          color: C.watch,
                          fontWeight: 600,
                          cursor: 'pointer',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        ❙❙ Pause
                      </span>
                    ))}
                  {l.meta_campaign_id && adAccount && (
                    <a
                      href={`https://adsmanager.facebook.com/adsmanager/manage/campaigns?act=${adAccount}&selected_campaign_ids=${l.meta_campaign_id}`}
                      target="_blank"
                      rel="noreferrer"
                      style={{
                        fontSize: 11.5,
                        color: C.basil,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Ads Manager →
                    </a>
                  )}
                  {/* A Meta content rule (licensed music, un-promotable post) cannot
                      be retried past — offering Retry here is what had an operator
                      rebuilding the same doomed campaign five times over. */}
                  {l.status === 'failed' && isUnretryableBuildError(l.error) && (
                    <span
                      title="Meta will refuse this post however many times it is rebuilt. Start a new boost from a different post."
                      style={{
                        fontSize: 11.5,
                        color: C.watch,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      Pick a different post
                    </span>
                  )}
                  {/* Resume a failed build with its original settings — one click,
                      nothing to re-pick (and no risk of rebuilding on the wrong audience). */}
                  {l.status === 'failed' && !isUnretryableBuildError(l.error) && (
                    <span
                      onClick={() => busyLaunch !== l.id && void retryBuild(l.id)}
                      title="Build again with the exact settings you originally chose"
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        fontSize: 11.5,
                        color: C.basil,
                        fontWeight: 600,
                        whiteSpace: 'nowrap',
                        cursor: busyLaunch === l.id ? 'default' : 'pointer',
                        opacity: busyLaunch === l.id ? 0.7 : 1,
                      }}
                    >
                      {busyLaunch === l.id ? (
                        <>
                          <Spinner size={11} color={C.basil} /> Rebuilding…
                        </>
                      ) : (
                        '↻ Retry build'
                      )}
                    </span>
                  )}
                  {confirmDel === l.id ? (
                    <span style={{ fontSize: 11.5, display: 'flex', gap: 8, whiteSpace: 'nowrap' }}>
                      <span
                        onClick={() => {
                          void deleteLaunch(l.id);
                          setConfirmDel(null);
                        }}
                        style={{ color: C.act, fontWeight: 600, cursor: 'pointer' }}
                      >
                        {l.status === 'failed' ? 'Dismiss?' : 'Delete?'}
                      </span>
                      <span
                        onClick={() => setConfirmDel(null)}
                        style={{ color: C.muted, cursor: 'pointer' }}
                      >
                        Cancel
                      </span>
                    </span>
                  ) : (
                    <span
                      onClick={() => setConfirmDel(l.id)}
                      title={
                        l.status === 'failed'
                          ? 'Dismiss and delete the failed campaign from Meta Ads Manager'
                          : 'Delete campaign (removes it from Meta too)'
                      }
                      style={{
                        fontSize: 11.5,
                        color: C.faint,
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {l.status === 'failed' ? 'Dismiss' : 'Delete'}
                    </span>
                  )}
                </Card>
              ))}
            </div>
          )}
          {orgId && <MetaCleanup orgId={orgId} />}
        </>
      )}
    </div>
  );
}

// A small spinning ring for in-flight actions (build / go live).
function Spinner({ size = 14, color = C.basil }: { size?: number; color?: string }) {
  return (
    <span
      style={{
        width: size,
        height: size,
        border: `2px solid ${alpha(color, 30)}`,
        borderTopColor: color,
        borderRadius: size,
        display: 'inline-block',
        animation: 'basilspin .7s linear infinite',
      }}
    />
  );
}

function Row({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex',
        gap: 16,
        alignItems: 'flex-start',
        padding: '10px 0',
        borderBottom: `1px solid ${C.lineSoft}`,
      }}
    >
      <div style={{ width: 190, flexShrink: 0, fontSize: 12.5, color: C.muted, paddingTop: 8 }}>
        {label}
      </div>
      <div style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function Stat({
  label,
  value,
  hint,
  small,
  color,
}: {
  label: string;
  value: string;
  hint?: string;
  small?: boolean;
  color?: string;
}) {
  return (
    <div title={hint}>
      <div
        style={{
          font: `500 9px 'IBM Plex Mono',monospace`,
          letterSpacing: '.1em',
          color: C.faint,
          marginBottom: 3,
        }}
      >
        {label}
      </div>
      <div style={{ fontSize: small ? 13 : 17, fontWeight: 600, color: color ?? C.primary }}>
        {value}
      </div>
    </div>
  );
}

function StatusDot({ status }: { status: string }) {
  const color =
    status === 'live'
      ? C.good
      : status === 'failed'
        ? C.act
        : status === 'paused'
          ? C.watch
          : C.muted;
  return (
    <span style={{ width: 9, height: 9, borderRadius: '50%', background: color, flexShrink: 0 }} />
  );
}

const inputStyle: React.CSSProperties = {
  width: '100%',
  boxSizing: 'border-box',
  background: C.surface2,
  border: `1px solid ${C.line}`,
  borderRadius: 9,
  padding: '9px 12px',
  fontSize: 13,
  color: C.primary,
  fontFamily: 'inherit',
};
const selectStyle: React.CSSProperties = { ...inputStyle };
const chipStyle: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  padding: '5px 10px',
  borderRadius: 8,
  fontSize: 12,
  color: C.secondary,
  border: `1px solid ${C.line}`,
};
